import { Directive, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { StepDirectionEnum } from '@enums/step-direction.enum';
import { PaginationPageType } from '@modules/table-pagination/types/pagination-page.type';

@Directive()
export class PaginationComponent implements OnChanges {
  @Input() itemsPerPage = 0;

  @Input() totalItemsCount = 0;

  @Input() currentPageNumber = 1;

  @Input() visiblePageCount = 5;

  @Output() currentPageNumberChange: EventEmitter<number> = new EventEmitter<number>();

  totalPageCount = 1;

  pagesToShow: PaginationPageType[] = [];

  readonly stepDirectionEnum: typeof StepDirectionEnum = StepDirectionEnum;

  /**
   * Calculate total page count and pagination pages only when totalItemsCount or itemsPerPage properties value change
   * @param simpleChanges - component input properties changes
   */
  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges.totalItemsCount?.currentValue || simpleChanges.itemsPerPage?.currentValue) {
      this.totalPageCount = Math.ceil(this.totalItemsCount / this.itemsPerPage);

      this.calculatePaginationPages();
    }
  }

  /**
   * TrackBy function to track pagination page by its value
   * @param _index - page index
   * @param item - page value (number or step direction enum)
   */
  trackByValue(_index: number, item: PaginationPageType): PaginationPageType {
    return item;
  }

  /**
   * Change current page number, emit it to the parent component and re-calculate pagination pages
   */
  onPageChange(pageNumber: number): void {
    if (pageNumber < 1 || pageNumber > this.totalPageCount) {
      return;
    }

    this.currentPageNumber = pageNumber;
    this.currentPageNumberChange.emit(pageNumber);

    this.calculatePaginationPages();
  }

  /**
   * Calculate pagination pages that we display. Dynamically calculates whether we need to show page indexes or step direction buttons
   */
  private calculatePaginationPages(): void {
    // Number of visible page numbers
    const visiblePageCount = this.visiblePageCount;

    // Return empty array if we don't have pages
    if (this.totalPageCount <= 1) {
      this.pagesToShow = [];

      return;
    }

    const pages: PaginationPageType[] = [];

    // Determine the start and end of the page range
    let startPage = Math.max(1, this.currentPageNumber - Math.floor(visiblePageCount / 2));
    let endPage = startPage + visiblePageCount - 1;

    // Handle the case when current page index is the last page
    if (endPage > this.totalPageCount) {
      endPage = this.totalPageCount;
      startPage = Math.max(1, endPage - visiblePageCount + 1);
    }

    // Push the page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add previous button if not on the first page range
    if (startPage > 1) {
      pages.splice(0, 1, StepDirectionEnum.PREVIOUS);
    }

    // Add next button if not in the last page range
    if (endPage < this.totalPageCount) {
      pages.splice(-1, 1, StepDirectionEnum.NEXT);
    }

    this.pagesToShow = pages;
  }
}
